'use client';

import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { getDate } from 'date-fns/getDate';
import { lastDayOfMonth } from 'date-fns/lastDayOfMonth';
import { sub } from 'date-fns/sub';
import { startOfMonth } from 'date-fns/startOfMonth';
import { RequestServiceCalendarProps, RequestServiceDateProps } from '../types';
import { CatList } from '@blocks-react/components/List';
import { PAGE_BREAKPOINTS } from '../../../constants/commonConstants';
import { CatIconChevronLeft } from '@blocks-react/components/Icons/ChevronLeft';
import { CatIconChevronRight } from '@blocks-react/components/Icons/ChevronRight';
import { useBreakpoint } from '../../../hooks';
import styles from './RequestServiceCalendar.module.scss';
import clx from 'classnames';
import { CalendarCarouselItem } from './calendar-carousel-item';

export const RequestServiceCalendar: FC<
  PropsWithChildren<RequestServiceCalendarProps>
> = ({
  onClickHandler,
  startDate = new Date(),
  selectedDates = [],
  weekStartDate,
  customDateError,
  setWeekStartDate,
  classname,
  selectedClassname
}) => {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [weekDays, setWeekDays] = useState<RequestServiceDateProps[]>([]);
  const noOfWeeks = useMemo(() => {
    if (weekDays.length > 0) {
      const noOfWeeks = weekDays.length / 7;
      return Math.ceil(noOfWeeks);
    }
    return 0;
  }, [weekDays.length]);
  const isSM = !useBreakpoint(PAGE_BREAKPOINTS.MD);

  const isEndOfDateRange = currentWeekIndex >= noOfWeeks - 1;
  const isStartOfDateRange = currentWeekIndex === 0;
  const prevBtn = () => {
    if (!isStartOfDateRange) {
      setCurrentWeekIndex((prev) => {
        if (prev - 1 === 0) {
          setWeekStartDate(sub(weekStartDate, { days: 7 }));
          return 0;
        }
        setWeekStartDate(sub(weekStartDate, { days: 7 }));
        return prev - 1;
      });
    }
  };
  const nextBtn = () => {
    if (!isEndOfDateRange) {
      setCurrentWeekIndex((prev) => {
        if (prev + 1 === noOfWeeks) {
          return noOfWeeks;
        }
        setWeekStartDate(addDays(weekStartDate, 7));
        return prev + 1;
      });
    }
  };
  const getWeekDays = (startDate: Date, isCurrentMonth: boolean) => {
    const weekDaysAdded: RequestServiceDateProps[] = [];
    const lastDay = lastDayOfMonth(startDate);
    const lastDate = isCurrentMonth
      ? lastDay.getDate() - new Date().getDate()
      : lastDay.getDate() - 1;

    for (let i = 0; i <= lastDate; i++) {
      const date = addDays(startDate, i);
      weekDaysAdded.push({
        day: format(date, 'EEE'),
        unFormattedDate: date,
        date: getDate(date),
        formattedDate: format(date, 'yyyy-MM-dd')
      });
    }
    setWeekDays(weekDaysAdded);
  };

  useEffect(() => {
    const isCurrentMonth = startDate.getMonth() === new Date().getMonth();
    const startFrom = isCurrentMonth ? new Date() : startOfMonth(startDate);
    getWeekDays(startFrom, isCurrentMonth);
    setCurrentWeekIndex(0);
  }, [startDate]);

  const slideClassName = clx(
    'pb-3 pb-md-0 pt-3',
    { 'overflow-auto': isSM },
    styles['calendar-container--carousel-inner'],
    styles[`calendar-container--carousel-slide--${currentWeekIndex}`]
  );

  return (
    <div className={clx({ 'request-service-error': customDateError })}>
      <div
        className={clx(
          `mt-3 d-flex align-items-center mx-auto`,
          styles['calendar-container']
        )}
      >
        {!isSM && (
          <button
            className={clx(
              'p-2 mt-3',
              styles['calendar-container--navigation-btn'],
              { [styles['calendar-container--isDisabled']]: isStartOfDateRange }
            )}
            type="button"
            disabled={isStartOfDateRange}
            data-testid="prev-nav-btn"
            onClick={prevBtn}
          >
            <CatIconChevronLeft size="sm" />
          </button>
        )}
        <div className={styles['calendar-container--carousel']}>
          <div className={slideClassName}>
            <CatList
              shadow="subtle"
              className={styles['calendar-container--carousel-list']}
            >
              {weekDays.map((weekDay: RequestServiceDateProps) => (
                <CalendarCarouselItem
                  key={weekDay.date}
                  dateItem={weekDay}
                  customDateError={customDateError}
                  onClickHandler={onClickHandler}
                  selectedDates={selectedDates}
                  classname={classname}
                  selectedClassname={selectedClassname}
                />
              ))}
            </CatList>
          </div>
        </div>
        {!isSM && (
          <button
            className={clx(
              'p-2 mt-3',
              styles['calendar-container--navigation-btn'],
              { [styles['calendar-container--isDisabled']]: isEndOfDateRange }
            )}
            type="button"
            disabled={isEndOfDateRange}
            data-testid="next-nav-btn"
            onClick={nextBtn}
          >
            <CatIconChevronRight size="sm" />
          </button>
        )}
      </div>
    </div>
  );
};
